import {
  NO_RUN_KEY,
  NO_TILE_KEY,
  SCIENTIFIC_STATUS_VALUES,
  STATUS_NONE,
  type ArtifactFacetValue,
  type ArtifactQuery,
  type GroupDimension,
} from "@/api/artifacts";

/**
 * URL state ↔ server query for the artifacts feed (flowstack#963).
 *
 * **Nothing here filters, groups or counts.** It used to: `filterArtifacts`,
 * `groupArtifacts` and `distinctValues` ran over one already-fetched page, so
 * every number they produced was a count of what had been loaded and every
 * option they offered was drawn from the same window — an artifact beyond the
 * page was unreachable and unmentioned, which is why the page had to carry a
 * standing warning about its own results. `GET /api/artifacts` now applies
 * each predicate in SQL before the limit and returns real totals, so this
 * module's whole job is translating between the address bar and that request.
 *
 * The URL is still the state, unchanged (`?q=&type=&run=&tile=&status=&group=
 * &page=`), so a filtered view survives paste, bookmark and reload.
 */

/**
 * The `?group=` vocabulary, unchanged from before server-side grouping.
 *
 * Deliberately still the short user-facing words rather than the server's
 * dimension names: these are in people's bookmarks and in the user docs, and
 * renaming them to match an internal wire field would break those to no one's
 * benefit. {@link GROUP_DIMENSIONS} maps them across.
 *
 * `project` is gone. Grouping by it produced a single section holding
 * everything, because every artifact carries the same placeholder slug
 * (flowstack#515) — and a grouping the server cannot count is precisely the
 * page-scoped number this page no longer prints.
 */
export type GroupBy = "none" | "run" | "type" | "tile" | "status";

export const GROUP_BY_VALUES: readonly GroupBy[] = ["none", "run", "type", "tile", "status"];

export function isGroupBy(value: string): value is GroupBy {
  return (GROUP_BY_VALUES as readonly string[]).includes(value);
}

/** `?group=` → the server dimension it groups by. */
export const GROUP_DIMENSIONS: Record<Exclude<GroupBy, "none">, GroupDimension> = {
  run: "run_id",
  type: "typed_object",
  tile: "producer_tile",
  status: "scientific_status",
};

export interface ArtifactFilters {
  /** Free-text needle, matched server-side across hash / type / tile / run. */
  q: string;
  typedObject: string;
  runId: string;
  producerTile: string;
  scientificStatus: string;
}

export const EMPTY_FILTERS: ArtifactFilters = {
  q: "",
  typedObject: "",
  runId: "",
  producerTile: "",
  scientificStatus: "",
};

/** The URL parameter each filter lives in. Also the set `clearFilters` drops. */
export const FILTER_PARAMS = ["q", "type", "run", "tile", "status"] as const;

export function filtersFromParams(params: URLSearchParams): ArtifactFilters {
  return {
    q: params.get("q") ?? "",
    typedObject: params.get("type") ?? "",
    runId: params.get("run") ?? "",
    producerTile: params.get("tile") ?? "",
    scientificStatus: params.get("status") ?? "",
  };
}

export function hasActiveFilters(filters: ArtifactFilters): boolean {
  return (
    filters.q.trim() !== "" ||
    filters.typedObject !== "" ||
    filters.runId !== "" ||
    filters.producerTile !== "" ||
    filters.scientificStatus !== ""
  );
}

/** Turn the page's URL state into the request the server answers. */
export function toArtifactQuery(
  filters: ArtifactFilters,
  options: { groupBy: GroupBy; offset: number; limit: number }
): ArtifactQuery {
  return {
    q: filters.q,
    typedObject: filters.typedObject,
    runId: filters.runId,
    producerTile: filters.producerTile,
    scientificStatus: filters.scientificStatus,
    limit: options.limit,
    offset: options.offset,
    // The controls are built from facet counts, so the page always asks for
    // them — that is what lets an option name a type no loaded row carries.
    facets: true,
    groupBy: options.groupBy === "none" ? undefined : GROUP_DIMENSIONS[options.groupBy],
  };
}

export interface FilterOption {
  value: string;
  label: string;
  count: number;
}

/**
 * Options for one filter control, from that dimension's facet counts.
 *
 * The absent bucket (facet `value: null`) becomes the dimension's sentinel, so
 * "Not produced by a run" is a real query rather than a client-side
 * afterthought. Values are otherwise passed through untouched — the route
 * forwards whatever the catalog holds, and a row carrying a writer's typo must
 * stay reachable.
 */
export function optionsFromFacet(
  facet: ArtifactFacetValue[] | undefined,
  absentValue: string,
  absentLabel: string
): FilterOption[] {
  return (facet ?? []).map((entry) =>
    entry.value === null
      ? { value: absentValue, label: absentLabel, count: entry.count }
      : { value: entry.value, label: entry.value, count: entry.count }
  );
}

/**
 * Options for the scientific-status control: the whole closed vocabulary,
 * ALWAYS.
 *
 * Not just the values the catalog currently holds (Rupert PR-259 r1): a filter
 * that enumerates only what it can see makes the omitted rows unreachable. A
 * status with no artifacts is offered with a count of 0 — which is itself
 * useful, since "no failed bundles" is an answer. Out-of-enum values actually
 * present are appended, because the route forwards `scientific_status`
 * without validating it.
 */
export function statusOptions(facet: ArtifactFacetValue[] | undefined): FilterOption[] {
  const counts = new Map<string | null, number>((facet ?? []).map((e) => [e.value, e.count]));
  const known: FilterOption[] = SCIENTIFIC_STATUS_VALUES.map((status) => ({
    value: status,
    label: status,
    count: counts.get(status) ?? 0,
  }));
  const extra: FilterOption[] = [...counts.entries()]
    .filter(
      ([value]) =>
        value !== null && !(SCIENTIFIC_STATUS_VALUES as readonly string[]).includes(value)
    )
    .map(([value, count]) => ({ value: value as string, label: value as string, count }));
  return [
    ...known,
    ...extra,
    { value: STATUS_NONE, label: "No status", count: counts.get(null) ?? 0 },
  ];
}

export function runOptions(facet: ArtifactFacetValue[] | undefined): FilterOption[] {
  return optionsFromFacet(facet, NO_RUN_KEY, "Not produced by a run");
}

export function tileOptions(facet: ArtifactFacetValue[] | undefined): FilterOption[] {
  return optionsFromFacet(facet, NO_TILE_KEY, "User-supplied");
}

/** Human label for one group heading. */
export function groupLabel(value: string | null, groupBy: GroupDimension): string {
  if (value !== null) return value;
  switch (groupBy) {
    case "run_id":
      return "Not produced by a run";
    case "producer_tile":
      return "User-supplied";
    case "scientific_status":
      return "No status";
    case "typed_object":
      // `type` is NOT NULL in the catalog, so this bucket cannot occur; the
      // arm exists so the switch stays exhaustive rather than falling through
      // to a label that would quietly misdescribe a real group.
      return "Untyped";
  }
}

/** Where a group heading links through to, when it names a real page. */
export function groupLink(value: string | null, groupBy: GroupDimension): string | null {
  if (groupBy === "run_id" && value !== null) return `/runs/${value}`;
  return null;
}

/** The label shown on the group-by control for each `?group=` value. */
export const GROUP_BY_LABELS: Record<Exclude<GroupBy, "none">, string> = {
  run: "Group by run",
  type: "Group by type",
  tile: "Group by producing tile",
  status: "Group by status",
};

/** The label shown on each filter control. */
export const FILTER_LABELS = {
  typedObject: "Filter by type",
  runId: "Filter by run",
  producerTile: "Filter by producing tile",
  scientificStatus: "Filter by scientific status",
} as const;
